import { ComponentInstance } from '@uniformdev/canvas';
import { type ComponentProps, UniformSlot } from '@uniformdev/canvas-react';
import { useIntl } from 'react-intl';
import { WacRefreshProvider } from 'src/utils/wac-refresh/wac-refresh-context.tsx';

import ProductTemplateContextProvider from '@/components/product-template-context-provider';
import { ProductNavBar } from '@/components/wpl/product-navbar/src/product-navbar';
import { DarkThemeProvider } from '@/libraries/design/utils/dark-theme-provider';
import { IntercomProvider } from '@/libraries/intercom/src/provider';
import { ONE_MIND_DEPLOYMENTS } from '@/libraries/one-mind/src/one-mind-launcher';
import { OneMindProvider } from '@/libraries/one-mind/src/one-mind-provider';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { GlobalFooter } from '@/patterns/global-footer/src/global-footer';
import { GlobalNav } from '@/patterns/global-nav/src/nav-global';
import { SimplifiedProductSubNav } from '@/patterns/global-nav/src/simplified-product-subnav';
import { BitbucketFooter } from '@/templates/bitbucket/bitbucket-footer.tsx';
import { CollectionsCompoundNav } from '@/templates/collections/collections-compound-nav.tsx';
import EnterprisePageProductNav from '@/templates/enterprise/enterprise-landing-page/sections/enterprise-nav';
import { JacNavbar } from '@/templates/jacapp/jac-navbar';
import { BitbucketNav } from '@/templates/software/bitbucket/src/sections/bitbucket-navbar.tsx';
import { CompassNav } from '@/templates/software/compass/src/sections/compass-navbar.tsx';
import ConfluenceSoftwareNav from '@/templates/software/confluence/confluence-software-page/src/sections/confluence-navbar';
import { ConfluenceTemplateHeaderContainer } from '@/templates/software/confluence/templates/confluence-templates-page/src/sections/shared-header-footer-container.tsx';
import { GuardNav } from '@/templates/software/guard/src/sections/guard-navbar.tsx';
import JiraProductDiscoveryNav from '@/templates/software/jira/product-discovery/jira-product-discovery-software-page/src/sections/product-nav.tsx';
import JiraSoftwareNav from '@/templates/software/jira/sections/nav-bar/software-jira-navbar';
import JiraSoftwareNavUniform from '@/templates/software/jira/sections/nav-bar/software-jira-navbar-uniform';
import JiraServiceManagementSoftwareNavV2 from '@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2';
import JiraServiceManagementSoftwareNavV2Uniform from '@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2-uniform';
import { LoomNav } from '@/templates/software/loom/pricing/sections/loom-navbar.tsx';
import { RovoNav } from '@/templates/software/rovo/rovo-nav.tsx';
import { SourcetreeAppFooter } from '@/templates/sourcetreeapp/sourcetree-app-footer.tsx';
import { SourcetreeAppNav } from '@/templates/sourcetreeapp/sourcetree-app-nav.tsx';
import { DwellTimeContextProvider } from '@/utils/dwell-tracking/dwell-tracker-context.tsx';
import { defaultTrackability } from '@/utils/dwell-tracking/effects/trackability';
import { experiments } from '@/utils/experiment/manifest';
import { EnrollmentType, useExperiment } from '@/utils/experiment/use-experiment';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { ChatWidgetProvider } from '../../../../components/chat-widget/src/chat-widget.tsx';
import { useLocale } from '../../../../libraries/web-framework/src/localization/use-locale';
import { containerTrackability } from '../../container/src/container.tsx';

import { CareerDetailsNavbar } from './navbars/career-details-navbar.tsx';
import { RovoDevNav } from './navbars/rovo-dev-navbar.tsx';
import { OptionalSiteSelectorContextProvider } from './optional-contexts.tsx';

/**
 * GWP-12151: maps the 1mind experiment cell to its vendor deployment id. The
 * `control` cell is intentionally absent (no launcher — keep the current chat).
 */
const ONE_MIND_DEPLOYMENT_BY_VARIANT: Partial<Record<string, string>> = {
	ai_guide: ONE_MIND_DEPLOYMENTS.aiGuide,
	stephen: ONE_MIND_DEPLOYMENTS.stephen,
};

export interface PageProps extends ComponentProps {
	pageTitle: string;
	metaDescription?: string;
	siteSelectorProductKey?: string;
	siteSelectorAnalyticsPrefix?: string;
	dwellTimeEnabled?: boolean;
	darkTheme?: boolean;
	wacRefresh?: boolean;
}

// Track both `data-dwell-id` elements (the default) and Uniform container
// roots, which are instrumented with `data-uniform-container-id`.
const DWELL_TRACKABILITY = [defaultTrackability, containerTrackability];

export const Page = (props: PageProps) => {
	const {
		component,
		siteSelectorProductKey,
		siteSelectorAnalyticsPrefix,
		dwellTimeEnabled,
		darkTheme,
		wacRefresh,
	} = props;

	const fullSiteSelectorProductKey =
		siteSelectorProductKey === 'jira-service-management'
			? 'jira-servicedesk.ondemand'
			: `${siteSelectorProductKey}.ondemand`;

	const locale = useLocale();

	const isBootIntercom: boolean = checkFeatureFlagGate(
		featureFlags.gwp5381AddIntercomWidgetToUniform,
	);

	const intercomAppId = locale === 'ja' ? 'p0ww63i2' : '';

	const isSalesWidgetBxpWebPlatform: boolean =
		intercomAppId !== '' && checkFeatureFlagGate(featureFlags.salesWidgetBxpWebPlatform);

	const contentSlot = component.slots?.['content'] ?? [];
	const navSlot = component.slots?.['nav'] ?? [];

	const pageNavigation =
		navSlot.length > 0 ? <UniformSlot name="nav" /> : <PageNav component={component} />;
	const pageHasGlobalFooterComponent = !!(contentSlot as ComponentInstance[]).find(
		(content) => content.type === 'globalFooter',
	);

	const isSalesWidgetBxpWebPlatformDeveloper: boolean = checkFeatureFlagGate(
		featureFlags.salesWidgetBxpWebPlatformDeveloper,
	);

	// GWP-12151: the experiment cohort scopes this to the JPD product-discovery
	// page only; on every other Uniform page it is `notEnrolled` and this is a
	// no-op. In the 1mind variant cells we replace the chat provider entirely
	// with the vendor launcher; `control` keeps today's Omnichannel/Intercom.
	const oneMind = useExperiment(experiments.gwp12151Superhuman1mind);
	const oneMindDeploymentId =
		oneMind.type === EnrollmentType.ENROLLED
			? ONE_MIND_DEPLOYMENT_BY_VARIANT[oneMind.value]
			: undefined;

	const pageContent = (
		<DarkThemeProvider enabled={!!darkTheme}>
			<ProductTemplateContextProvider>
				<OptionalSiteSelectorContextProvider
					siteSelectorProductKey={fullSiteSelectorProductKey}
					siteSelectorAnalyticsPrefix={siteSelectorAnalyticsPrefix}
				>
					<TypographyStyles>
						<UniformSlot name="enhancedBannerSlot" />
					</TypographyStyles>
					{pageNavigation}
					<TypographyStyles>
						<main id="content">
							<UniformSlot name="content" />
						</main>
					</TypographyStyles>
				</OptionalSiteSelectorContextProvider>
			</ProductTemplateContextProvider>
			{!pageHasGlobalFooterComponent && <PageFooter component={component} />}
		</DarkThemeProvider>
	);

	if (oneMindDeploymentId) {
		return (
			<WacRefreshProvider isWacRefresh={wacRefresh}>
				<DwellTimeContextProvider enabled={!!dwellTimeEnabled} trackability={DWELL_TRACKABILITY}>
					<OneMindProvider
						deploymentId={oneMindDeploymentId}
						variantName={oneMind.type === EnrollmentType.ENROLLED ? oneMind.value : undefined}
					>
						{pageContent}
					</OneMindProvider>
				</DwellTimeContextProvider>
			</WacRefreshProvider>
		);
	}

	if (isSalesWidgetBxpWebPlatformDeveloper || isSalesWidgetBxpWebPlatform) {
		return (
			<WacRefreshProvider isWacRefresh={wacRefresh}>
				<DwellTimeContextProvider enabled={!!dwellTimeEnabled} trackability={DWELL_TRACKABILITY}>
					<ChatWidgetProvider>{pageContent}</ChatWidgetProvider>
				</DwellTimeContextProvider>
			</WacRefreshProvider>
		);
	}

	return (
		<WacRefreshProvider isWacRefresh={wacRefresh}>
			<DwellTimeContextProvider enabled={!!dwellTimeEnabled} trackability={DWELL_TRACKABILITY}>
				<IntercomProvider appId={intercomAppId} triggerBoot={isBootIntercom}>
					{pageContent}
				</IntercomProvider>
			</DwellTimeContextProvider>
		</WacRefreshProvider>
	);
};

// eslint-disable-next-line complexity
export const PageNav = ({ component }: ComponentProps) => {
	const intl = useIntl();

	const pageNavType =
		component.parameters && component.parameters['nav'] && component.parameters['nav'].value;
	let pageNav;
	switch (pageNavType) {
		case 'confluence':
			pageNav = <ConfluenceSoftwareNav />;
			break;
		case 'bitbucket':
			pageNav = <BitbucketNav />;
			break;
		case 'confluence-template-experiment':
			pageNav = <ConfluenceTemplateHeaderContainer />;
			break;
		case 'collections':
			pageNav = <CollectionsCompoundNav />;
			break;
		case 'enterprise':
			pageNav = <EnterprisePageProductNav />;
			break;
		case 'jira-software':
			pageNav = <JiraSoftwareNav />;
			break;
		case 'jira-service-management':
			pageNav = <JiraServiceManagementSoftwareNavV2 />;
			break;
		case 'jira-product-discovery':
			pageNav = <JiraProductDiscoveryNav />;
			break;
		case 'gwp-7880-jira-software-templates-experiment':
			pageNav = <JiraSoftwareNavUniform />;
			break;
		case 'gwp-7880-jira-service-management-templates-experiment':
			pageNav = <JiraServiceManagementSoftwareNavV2Uniform />;
			break;
		case 'compass':
			pageNav = <CompassNav />;
			break;
		case 'atlassian-access':
		case 'guard':
			pageNav = <GuardNav />;
			break;
		case 'rovo':
			pageNav = <RovoNav />;
			break;
		case 'rovo-dev':
			pageNav = <RovoDevNav />;
			break;
		case 'career-details':
			pageNav = <CareerDetailsNavbar />;
			break;
		case 'loom':
			pageNav = <LoomNav />;
			break;
		case 'sourcetree-app':
			pageNav = <SourcetreeAppNav />;
			break;
		case 'jac':
			pageNav = <JacNavbar />;
			break;
		case 'jsm-anchor':
			pageNav = (
				<ProductNavBar
					productHref="/software/jira/service-management"
					productName="jiraservicemanagement"
					ariaLabel={intl.formatMessage({
						id: 'wacJiraServiceManagementNavigation.logo-label',
						defaultMessage: 'Jira Service Management logo',
					})}
					dataProductKey="jiraServiceManagement"
					showFreeHrefButton={false}
					logoLinkWrapperWidth="279px"
				/>
			);
			break;
		case 'simplified-jira-software':
			pageNav = (
				<SimplifiedProductSubNav
					productLogoUrl="/software/jira"
					productLogoAriaLabel={intl.formatMessage({
						id: 'wacJiraSoftwareNavigation.logo-label',
						defaultMessage: 'Jira Software logo',
					})}
					productLogo={
						<LogoV3 brand="jira" type="logo" appearance="brand" height="32px" width="83px" />
					}
					productKey="jira"
					isShowTryNowLink={false}
				/>
			);
			break;
		case 'global-nav-no-try-now-cta':
			pageNav = <GlobalNav productKey="uniform" isShowTryNowLink={false} />;
			break;
		case 'none':
			break;
		case 'global':
		default:
			pageNav = <GlobalNav productKey="uniform" isShowTryNowLink={true} />;
			break;
	}

	return pageNav;
};

export const PageFooter = ({ component }: ComponentProps) => {
	const pageFooterType =
		component.parameters && component.parameters['footer'] && component.parameters['footer'].value;
	let pageFooter;
	switch (pageFooterType) {
		case 'bitbucket':
			pageFooter = <BitbucketFooter />;
			break;
		case 'sourcetree-app':
			pageFooter = <SourcetreeAppFooter />;
			break;
		case 'none':
			break;
		case 'global':
		default:
			pageFooter = <GlobalFooter />;
			break;
	}

	return pageFooter;
};

export default Page;
